/**
 * Cart drawer recommendations: fetches product recommendations based on the first
 * product in the cart and renders them in the drawer, excluding products already in cart.
 */

const SECTION_ID = 'cart-drawer-recommendations';
const INTENT = 'related';
const DEFAULT_LIMIT = 4;

class CartDrawerRecommendations extends HTMLElement {
  /** @type {AbortController | null} */
  #fetchController = null;

  /** @type {string} */
  #lastCartKey = '';

  connectedCallback() {
    this.#runWhenDrawerOrCartReady();
  }

  disconnectedCallback() {
    this.#fetchController?.abort();
  }

  /**
   * Wait for cart drawer to exist and cart to be available, then load or refresh.
   */
  #runWhenDrawerOrCartReady() {
    const drawer = this.closest('cart-drawer-component');
    if (drawer) {
      this.#loadRecommendations();
      this.#observeDrawerAndCart(drawer);
    }
  }

  /**
   * Observe drawer open and cart updates to refresh recommendations.
   * @param {HTMLElement} drawer
   */
  #observeDrawerAndCart(drawer) {
    drawer.addEventListener('dialog:open', () => this.#loadRecommendations());
    document.addEventListener('cart:update', () => this.#loadRecommendations());
  }

  /**
   * Load recommendations using first cart product as seed; filter out cart products.
   */
  async #loadRecommendations() {
    const baseUrl = this.dataset.recommendationsUrl;
    if (!baseUrl) return;

    const limit = this.dataset.limit ? parseInt(this.dataset.limit, 10) : DEFAULT_LIMIT;
    const cartProductIds = await this.#getCartProductIds();
    if (cartProductIds.length === 0) {
      this.#setEmpty();
      return;
    }

    const seedProductId = cartProductIds[0];
    const cartKey = cartProductIds.sort().join(',');
    if (cartKey === this.#lastCartKey && this.#hasContent()) {
      return;
    }
    this.#lastCartKey = cartKey;

    this.#setLoading();
    this.#fetchController?.abort();
    this.#fetchController = new AbortController();

    const url = `${baseUrl}?limit=${limit + cartProductIds.length}&section_id=${SECTION_ID}&product_id=${seedProductId}&intent=${INTENT}`;

    try {
      const response = await fetch(url, { signal: this.#fetchController.signal });
      if (!response.ok) {
        this.#setEmpty();
        return;
      }
      const html = await response.text();
      this.#renderResponse(html, cartProductIds);
    } catch (err) {
      if (err.name === 'AbortError') return;
      this.#setEmpty();
    } finally {
      this.#fetchController = null;
    }
  }

  /**
   * @returns {Promise<string[]>} Cart item product IDs.
   */
  async #getCartProductIds() {
    try {
      const res = await fetch('/cart.js');
      const cart = await res.json();
      if (!cart?.items?.length) return [];
      return cart.items.map((item) => String(item.product_id));
    } catch {
      return [];
    }
  }

  /**
   * Parse section HTML and inject list, excluding items already in cart.
   * @param {string} html
   * @param {string[]} cartProductIds
   */
  #renderResponse(html, cartProductIds) {
    const wrap = document.createElement('div');
    wrap.innerHTML = html;

    const list = wrap.querySelector('[data-cart-drawer-recommendations-content]');
    if (!list) {
      this.#setEmpty();
      return;
    }

    const track = list.querySelector('.cart-drawer-recommendations__track');
    if (!track) {
      this.#setEmpty();
      return;
    }

    const items = track.querySelectorAll('.cart-drawer-recommendations__item[data-product-id]');
    const keep = [];
    items.forEach((item) => {
      const id = item.getAttribute('data-product-id');
      if (id && !cartProductIds.includes(id)) keep.push(item);
    });

    if (keep.length === 0) {
      this.#setEmpty();
      return;
    }

    track.innerHTML = '';
    keep.forEach((el) => track.appendChild(el));
    this.innerHTML = '';
    this.appendChild(list);
    this.#setupSlider(list);
    this.removeAttribute('aria-busy');
    this.removeAttribute('data-empty');
  }

  /**
   * Attach prev/next button scroll behavior to the slider.
   * @param {HTMLElement} list
   */
  #setupSlider(list) {
    const track = list.querySelector('.cart-drawer-recommendations__track');
    const prevBtn = list.querySelector('.cart-drawer-recommendations__nav-prev');
    const nextBtn = list.querySelector('.cart-drawer-recommendations__nav-next');
    if (!track || !prevBtn || !nextBtn) return;

    const scrollStep = 180;
    prevBtn.addEventListener('click', () => {
      track.scrollBy({ left: -scrollStep, behavior: 'smooth' });
    });
    nextBtn.addEventListener('click', () => {
      track.scrollBy({ left: scrollStep, behavior: 'smooth' });
    });
  }

  #setLoading() {
    this.removeAttribute('data-empty');
    this.setAttribute('aria-busy', 'true');
    this.innerHTML = '';
    const loader = document.createElement('div');
    loader.className = 'cart-drawer-recommendations__loading';
    loader.setAttribute('aria-hidden', 'true');
    this.appendChild(loader);
  }

  #setEmpty() {
    this.removeAttribute('aria-busy');
    this.setAttribute('data-empty', '');
    this.innerHTML = '';
  }

  #hasContent() {
    return this.querySelector('.cart-drawer-recommendations__list')?.children?.length > 0;
  }
}

if (!customElements.get('cart-drawer-recommendations')) {
  customElements.define('cart-drawer-recommendations', CartDrawerRecommendations);
}
